import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useCachedFetch } from '../hooks/useCachedFetch';
import {
  FATWA_API, ARTICLE_API, cachedFetch, LECTURES_API, RESEARCH_API, STATS_API, STORAGE_BASE,
  fatwaNum, catIcon, trunc, stripHtml, scholarTitledName, articleDate, lectureImg,
  LECTURE_GRADIENTS, SCHOLAR_COLORS, scholarInitial
} from '../utils/api';

const AR_NUMS = ['١','٢','٣','٤','٥'];

// Returns the localised name of a category object based on current lang
function catName(c, lang) {
  if (!c) return '';
  if (lang === 'ar') return c.name_ar || '';
  if (c.translations && c.translations[lang] && c.translations[lang].name) {
    return c.translations[lang].name;
  }
  if (lang === 'en') return c.name_en || c.name_ar || '';
  return c.name_ar || '';
}

// Given a fatwa `f` and the categories list, return the localised category label
function fatwaCatName(f, categories, lang, fallback = '') {
  if (!f) return fallback;
  const cat = (categories || []).find(c =>
    c.id === f.category_id ||
    c.name_ar === f.category_name ||
    c.name_en === f.category_name
  );
  if (cat) return catName(cat, lang);
  return f.category_name || fallback;
}

function Skel({ cls, style }) { return <div className={`skel ${cls||''}`} style={style} />; }

// "No content" message — shown when API responded but returned nothing
function NoContent({ icon, msgKey }) {
  const { t } = useLang();
  const msgs = {
    ar: { fatwas: 'لا توجد فتاوى بهذه اللغة حالياً', articles: 'لا توجد مقالات بهذه اللغة حالياً', lectures: 'لا توجد محاضرات بهذه اللغة حالياً', research: 'لا توجد أبحاث بهذه اللغة حالياً' },
    en: { fatwas: 'No fatwas available in this language yet.', articles: 'No articles available yet.', lectures: 'No lectures available yet.', research: 'No research available yet.' },
    ur: { fatwas: 'اس زبان میں ابھی کوئی فتویٰ دستیاب نہیں ہے۔', articles: 'اس زبان میں ابھی کوئی مضمون دستیاب نہیں ہے۔', lectures: 'اس زبان میں ابھی کوئی لیکچر دستیاب نہیں ہے۔', research: 'اس زبان میں ابھی کوئی تحقیق دستیاب نہیں ہے۔' },
  };
  const lang = t('topbar.tagline').includes('Washington') ? 'en' : t('topbar.tagline').includes('واشنگٹن') ? 'ur' : 'ar';
  return (
    <div style={{ padding: '1.2rem', color: 'var(--ink3)', fontSize: 'var(--t-sm)', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
      <span>{icon}</span> {msgs[lang]?.[msgKey] || msgs.ar[msgKey]}
    </div>
  );
}

function HadithWidget({ title, text, refText }) {
  return (
    <div className="hadith-widget">
      <div className="geo-sm" />
      <div className="title">{title}</div>
      <p>{text}</p>
      <div className="ref">{refText}</div>
    </div>
  );
}


// ─── Language version helpers ─────────────────────────────────────────────
// Fatwas: root record always has language='ar'. Translations live in
// f.versions = { ar:{question,answer,full_text}, en:{...}, ur:{...} }
// IMPORTANT: fatwas have no 'title' field — only 'question'.
// We set title=question so any code doing `f.title||f.question` works.
function _applyFatwa(f, lang) {
  const v = (f.versions||{})[lang] || (f.versions||{}).ar || {};
  const q = v.question || f.question || '';
  return { ...f, question: q, title: q, answer: v.answer||f.answer||'', full_text: v.full_text||f.full_text||'' };
}
function _filterFatwas(arr, lang) {
  return (arr||[])
    .filter(f => lang==='ar' || (f.versions && f.versions[lang] && f.versions[lang].question))
    .map(f => _applyFatwa(f, lang));
}
// ──────────────────────────────────────────────────────────────────────────
export default function HomePage({ scholars, categories, lang }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('');
  const [activeCatId, setActiveCatId] = useState(null);

  // Fetch fatwas for active category tab using the hook (auto-updates, cached)
  const tabUrl = activeCatId ? `${FATWA_API}?page=1&category_id=${activeCatId}&language=${lang}` : null;
  const { data: tabRaw, loading: tabLoading } = useCachedFetch(tabUrl);
  const tabFatwas = activeCatId ? _filterFatwas(tabRaw?.data, lang).slice(0, 6) : [];

  // Reset tab when language changes
  useEffect(() => {
    setActiveCatId(null);
    setActiveTab('');
  }, [lang]);

  const { data: statsRaw }                           = useCachedFetch(STATS_API);
  const { data: fatwaRaw,   loading: fatwaLoading   } = useCachedFetch(`${FATWA_API}?page=1&language=${lang}`);
  const { data: articleRaw, loading: articleLoading } = useCachedFetch(`${ARTICLE_API}?page=1&language=${lang}`);
  const { data: lectureRaw, loading: lectureLoading } = useCachedFetch(`${LECTURES_API}?page=1&language=${lang}`);
  const { data: researchRaw,loading: researchLoading} = useCachedFetch(`${RESEARCH_API}?page=1&language=${lang}`);

  const stats    = statsRaw?.data || statsRaw || {};
  const fatwas   = _filterFatwas(fatwaRaw?.data, lang);
  const articles = (articleRaw?.data || []).filter(a => {
    if (a.language !== lang) return false;
    if (a.title === 'wxwxx' || a.title === 'qqsqs') return false;
    return true;
  }).slice(0, 4);
  const lectures = (Array.isArray(lectureRaw)  ? lectureRaw  : (lectureRaw?.data  || [])).filter(l => l.language === lang).slice(0, 3);
  const research = (Array.isArray(researchRaw) ? researchRaw : (researchRaw?.data || [])).filter(r => r.language === lang && (r.title || '').trim()).slice(0, 3);

  // fatwaRaw !== null means the API responded (even if empty)
  const fatwasDone   = !fatwaLoading   && fatwaRaw   !== null;
  const articlesDone = !articleLoading && articleRaw  !== null;
  const lecturesDone = !lectureLoading && lectureRaw  !== null;
  const researchDone = !researchLoading&& researchRaw !== null;

  const displayFatwas  = activeCatId ? tabFatwas  : fatwas.slice(0, 6);
  const displayLoading = activeCatId ? tabLoading : fatwaLoading;
  const featuredFatwa = fatwas[0] || null;

  return (
    <div id="page-home">

      {/* HERO */}
      <div className="hero">
        <div className="hero-main">
          <div className="bg-img">
          <img src="/images/background-image.webp" alt="" fetchpriority="high" width="1600" height="983" />

          </div>
          <div className="hero-main-ov" /><div className="geo-lg" />
          <div className="hero-content">
            <div className="hero-badge">{t('hero.badge')}</div>
            <h1 className="hero-title">{t('hero.title1')}<br /><em>{t('hero.title2')}</em></h1>
            <p className="hero-excerpt">{t('hero.excerpt')}</p>
            <div className="hero-actions">
              <button className="btn-primary" onClick={() => navigate('/fatwas')}>{t('hero.btn.fatwas')}</button>
              <button className="btn-ghost"   onClick={() => navigate('/articles')} style={{ borderColor: 'rgba(192,57,43,.5)', color: '#f0a090' }}>{t('hero.btn.causes')}</button>
              <button className="btn-ghost"   onClick={() => navigate('/lectures')}>{t('hero.btn.youth')}</button>
            </div>
          </div>
        </div>

        {/* Hero top list */}
        <div className="hero-top-list">
          <div className="htl-head">{t('hero.top')}</div>
          {fatwaLoading
            ? AR_NUMS.map(n => (
                <div key={n} className="htl-item">
                  <div className="htl-num">{n}</div>
                  <Skel cls="skel-line" style={{ flex: 1, height: 13 }} />
                </div>
              ))
            : fatwas.length > 0
              ? fatwas.slice(0, 5).map((f, i) => (
                  <div key={f.id} className="htl-item" onClick={() => navigate(`/fatwas/${f.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="htl-num">{lang === 'ar' ? AR_NUMS[i] : i + 1}</div>
                    <a href={`/fatwas/${f.id}`} onClick={e => e.preventDefault()} style={{flex:1,minWidth:0,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',fontSize:'var(--t-sm)',lineHeight:'1.75',wordBreak:'break-word',whiteSpace:'normal'}}>{stripHtml(f.title || f.question || '')}</a>
                  </div>
                ))
              : <div style={{ padding: '.8rem', color: 'rgba(255,255,255,.4)', fontSize: 'var(--t-sm)' }}>{t('fb.loading').replace('…','')}</div>
          }
        </div>
      </div>

      {/* STATS */}
      <div className="stats-strip">
        {[
          { key: 'fatwas',          val: stats.fatwas          ? '+'+stats.fatwas : '…', label: t('stats.fatwas'),   path: '/fatwas'   },
          { key: 'researchers',     val: stats.researchers     || '…',                    label: t('stats.scholars'), path: '/scholars' },
          { key: 'articles',        val: stats.articles        || '…',                    label: t('stats.articles'), path: '/articles' },
          { key: 'lectures',        val: stats.lectures        || '…',                    label: t('stats.lectures'), path: '/lectures' },
          { key: 'research_papers', val: stats.research_papers || '…',                    label: t('stats.research'), path: '/research' },
        ].map(s => (
          <div key={s.key} className="stat-cell stat-clickable" onClick={() => navigate(s.path)}>
            <div className="stat-n">{s.val}</div>
            <div className="stat-l">{s.label}</div>
          </div>
        ))}
      </div>

      {/* 3-COLUMN GRID */}
      <div className="pw">
        <div className="home-grid">

          {/* LEFT SIDEBAR */}
          <aside className="sidebar-left">
            {lang === 'ar' && scholars.length > 0 && (
              <div className="sbox">
                <div className="sbox-head">{t('sidebar.scholars')}</div>
                <div className="sbox-body">
                  {scholars.slice(0, 5).map((s, i) => (
                    <div key={s.id} className="scholar-mini" onClick={() => navigate(`/scholars/${s.id}`)} style={{ cursor: 'pointer' }}>
                      <div className="sm-avi" style={{ background: SCHOLAR_COLORS[i % SCHOLAR_COLORS.length], width: 72, height: 72, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', fontFamily: 'var(--fam-display)', color: '#fff', fontSize: '1.7rem', flexShrink: 0 }}>
                        {s.photo
                          ? <img src={s.photo.startsWith('http') ? s.photo : STORAGE_BASE + s.photo} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : scholarInitial(s.name)}
                      </div>
                      <div>
                        <div className="sm-name">{scholarTitledName(s.name, lang)}</div>
                        <div className="sm-spec">{s.role || t('label.scholar.role')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {categories.length > 0 && (
              <div className="sbox">
                <div className="sbox-head">{t('sidebar.cats')}</div>
                <div className="cat-pills">
                  {categories.map(c => (
                    <span key={c.id} className="cat-pill" onClick={() => navigate(`/fatwas?cat=${c.id}`)}>{catName(c, lang)}</span>
                  ))}
                </div>
              </div>
            )}
            <HadithWidget title={t('sidebar.hadith.title')} text={t('sidebar.hadith.text')} refText={t('sidebar.hadith.ref')} />
          </aside>

          {/* MAIN */}
          <main>

            {/* Featured Fatwa */}
            <div className="sec-band">
              <h2><span className="bar" />{t('sec.featured.fatwa')}</h2>
            </div>
            {fatwaLoading ? (
              <div className="feat-card">
                <div className="feat-card-content">
                  <div className="feat-tag"><Skel style={{ width: 200, height: 20 }} /></div>
                  <Skel cls="skel-line" style={{ width: '85%', height: 24, marginTop: '.8rem' }} />
                  <Skel cls="skel-line" style={{ width: '70%', height: 16, marginTop: '.5rem' }} />
                </div>
              </div>
            ) : featuredFatwa ? (
              <div className="feat-card" onClick={() => navigate(`/fatwas/${featuredFatwa.id}`)} style={{ cursor: 'pointer' }}>
                <div className="geo-sm" />
                <div className="feat-card-content">
                  <div className="feat-tag">{fatwaCatName(featuredFatwa, categories, lang, t('label.general'))} · {fatwaNum(featuredFatwa)}</div>
                  <h3>{trunc(featuredFatwa.title || featuredFatwa.question, 120)}</h3>
                  <p>{trunc(featuredFatwa.answer || featuredFatwa.full_text, 140)}</p>
                </div>
                <div className="feat-card-deco">ف</div>
              </div>
            ) : (
              // API responded, no data for this language
              <div className="feat-card" style={{ cursor: 'default', opacity: .7 }}>
                <div className="feat-card-content">
                  <NoContent icon="📭" msgKey="fatwas" />
                </div>
              </div>
            )}

            {/* Fatwa Bank */}
            <div className="sec-band">
              <h2><span className="bar" />{t('sec.fatwa.bank')}</h2>
              <a href="/fatwas" className="more-btn" onClick={e => { e.preventDefault(); navigate('/fatwas'); }}>{t('sec.fatwa.viewall')}</a>
            </div>
            <div className="tabs-bar">
              <button className={`tab${!activeCatId?' active':''}`} onClick={() => { setActiveCatId(null); setActiveTab(''); }}>{t('home.tabs.all')}</button>
              {categories.map(cat => (
                <button key={cat.id}
                  className={`tab${activeCatId===cat.id?' active':''}`}
                  onClick={() => { setActiveTab(catName(cat, lang)); setActiveCatId(cat.id); }}>
                  {catName(cat, lang).length > 14 ? catName(cat, lang).slice(0, 13)+'…' : catName(cat, lang)}
                </button>
              ))}
            </div>
            <div className="fgrid">
              {displayLoading
                ? Array(6).fill(0).map((_, i) => (
                    <div key={i} className="fcard" style={{ pointerEvents: 'none' }}>
                      <Skel cls="skel-ico" />
                      <div style={{ flex: 1 }}><Skel cls="skel-tag" /><Skel cls="skel-line" style={{ width: '80%', marginTop: '.35rem' }} /></div>
                    </div>
                  ))
                : displayFatwas.length > 0
                  ? displayFatwas.map(f => (
                      <div key={f.id} className="fcard" onClick={() => navigate(`/fatwas/${f.id}`)} style={{ cursor: 'pointer' }}>
                        <div className="fcard-ico">{catIcon(f.category_name, categories)}</div>
                        <div>
                          <div className="fcard-cat">{fatwaCatName(f, categories, lang, t('label.general'))}</div>
                          <a href={`/fatwas/${f.id}`} onClick={e => e.preventDefault()}>{trunc(f.title || f.question, 80)}</a>
                          <div className="fcard-num">{fatwaNum(f)}</div>
                        </div>
                      </div>
                    ))
                  : <div style={{ gridColumn: '1/-1' }}><NoContent icon="📭" msgKey="fatwas" /></div>
              }
            </div>
            <a href="/fatwas" className="load-more-btn" onClick={e => { e.preventDefault(); navigate('/fatwas'); }}>{t('sec.fatwa.more')}</a>

            {/* Articles */}
            <div className="sec-band">
              <h2><span className="bar" />{t('sec.opinions')}</h2>
              <a href="/articles" className="more-btn" onClick={e => { e.preventDefault(); navigate('/articles'); }}>{t('sec.opinions.more')}</a>
            </div>
            <div className="alist">
              {articleLoading
                ? [1,2].map(i => (
                    <div key={i} className="alist-item" style={{ pointerEvents: 'none' }}>
                      <div className="alist-thumb"><Skel style={{ width: '100%', height: '100%' }} /></div>
                      <div style={{ flex: 1 }}><Skel cls="skel-tag" /><Skel cls="skel-line" style={{ width: '80%', marginTop: '.35rem' }} /></div>
                    </div>
                  ))
                : articles.length > 0
                  ? articles.map(a => {
                      const img = a.featured_image || a.image || '';
                      const imgUrl = img ? (img.startsWith('http') ? img : STORAGE_BASE+img) : '';
                      return (
                        <div key={a.id} className="alist-item" onClick={() => navigate(`/articles/${a.id}`)} style={{ cursor: 'pointer' }}>
                          <div className="alist-thumb" style={!imgUrl ? { background: 'linear-gradient(135deg,var(--g1),var(--g3))', display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}>
                            {imgUrl
                              ? <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              : <span style={{ fontFamily: 'var(--fam-display)', fontSize: '1.8rem', color: 'rgba(255,255,255,.5)' }}>{(a.title||'').charAt(0)}</span>
                            }
                          </div>
                          <div>
                            <div className="alist-cat">{a.category || 'مقال'}</div>
                            <a href={`/articles/${a.id}`} className="alist-title" onClick={e => e.preventDefault()}>{a.title || trunc(a.content, 70) || 'مقال'}</a>
                            <div className="alist-exc">{trunc(a.content, 80)}</div>
                            <div className="alist-time"><span>{articleDate(a, lang)}</span></div>
                          </div>
                        </div>
                      );
                    })
                  : <NoContent icon="📝" msgKey="articles" />
              }
            </div>

            {/* Lectures */}
            <div className="sec-band">
              <h2><span className="bar" />{t('sec.lectures.latest')}</h2>
              <a href="/lectures" className="more-btn" onClick={e => { e.preventDefault(); navigate('/lectures'); }}>{t('sec.lectures.more')}</a>
            </div>
            <div className="media-row">
              {lectureLoading
                ? [1,2,3].map(i => (
                    <div key={i} className="mcard" style={{ pointerEvents: 'none' }}>
                      <div className="mcard-thumb"><Skel style={{ width: '100%', height: '100%' }} /></div>
                      <div className="mcard-body"><Skel cls="skel-line" style={{ width: '80%', height: 15 }} /></div>
                    </div>
                  ))
                : lectures.length > 0
                  ? lectures.map((l, i) => {
                      const img = lectureImg(l);
                      return (
                        <div key={l.id} className="mcard" onClick={() => navigate(`/lectures/${l.id}`)} style={{ cursor: 'pointer' }}>
                          <div className="mcard-thumb" style={img ? { backgroundImage: `url('${img}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: LECTURE_GRADIENTS[i%LECTURE_GRADIENTS.length] }}>
                            {!img && <div className="play-btn">▶</div>}
                          </div>
                          <div className="mcard-body">
                            <div className="mcard-title">{trunc(l.title||'…', 70)}</div>
                            <div className="mcard-meta">{[l.speaker,l.category].filter(Boolean).join(' · ')}</div>
                          </div>
                        </div>
                      );
                    })
                  : <NoContent icon="🎬" msgKey="lectures" />
              }
            </div>

            {/* Research */}
            <div className="sec-band">
              <h2><span className="bar" />{t('sec.research.latest')}</h2>
              <a href="/research" className="more-btn" onClick={e => { e.preventDefault(); navigate('/research'); }}>{t('sec.research.more')}</a>
            </div>
            <div className="media-row">
              {researchLoading
                ? [1,2,3].map(i => (
                    <div key={i} className="mcard" style={{ pointerEvents: 'none' }}>
                      <div className="mcard-thumb"><Skel style={{ width: '100%', height: '100%' }} /></div>
                      <div className="mcard-body"><Skel cls="skel-line" style={{ width: '80%', height: 15 }} /></div>
                    </div>
                  ))
                : research.length > 0
                  ? research.map((r, i) => {
                      const img = r.cover_image ? (r.cover_image.startsWith('http') ? r.cover_image : STORAGE_BASE+r.cover_image) : '';
                      return (
                        <div key={r.id} className="mcard">
                          <div className="mcard-thumb" style={img ? { backgroundImage: `url('${img}')`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: LECTURE_GRADIENTS[i%LECTURE_GRADIENTS.length] }}>
                            {!img && <div className="play-btn">📄</div>}
                          </div>
                          <div className="mcard-body">
                            <div className="mcard-title">{trunc(r.title||'…', 70)}</div>
                            <div className="mcard-meta">{[r.authors,r.category].filter(Boolean).join(' · ')}</div>
                          </div>
                        </div>
                      );
                    })
                  : <NoContent icon="📄" msgKey="research" />
              }
            </div>

            <div className="quote-band">
              <div className="geo-lg" />
              <p>{t('quote.band')}</p>
            </div>
          </main>

          {/* RIGHT SIDEBAR */}
          <aside className="sidebar-right">
            <HadithWidget title={t('sidebar.ayah.title')} text={t('sidebar.ayah.text')} refText={t('sidebar.ayah.ref')} />
            <div className="donate-widget">
              <h4>{t('sidebar.donate.title')}</h4>
              <p>{t('sidebar.donate.text')}</p>
              <a href="/donate" className="dw-btn" onClick={e=>{e.preventDefault();navigate('/donate');}}>{t('sidebar.donate.stripe')}</a>
              <a href="/donate" className="dw-btn" onClick={e=>{e.preventDefault();navigate('/donate');}}>{t('sidebar.donate.square')}</a>
            </div>
            <div className="sbox">
              <div className="sbox-head">{t('sidebar.popular')}</div>
              <div className="sbox-body">
                {fatwaLoading
                  ? [1,2,3,4,5].map(i => (
                      <div key={i} className="slist-item" style={{ pointerEvents: 'none' }}>
                        <Skel style={{ width: 32, height: 18, borderRadius: 4, flexShrink: 0 }} />
                        <Skel cls="skel-sb-line" />
                      </div>
                    ))
                  : fatwas.length > 0
                    ? [...fatwas].sort(()=>Math.random()-.5).slice(0,7).map(f => (
                        <div key={f.id} className="slist-item" onClick={() => navigate(`/fatwas/${f.id}`)} style={{ cursor: 'pointer' }}>
                          {fatwaNum(f) && <div className="slist-n">{fatwaNum(f)}</div>}
                          <a href={`/fatwas/${f.id}`} onClick={e=>e.preventDefault()} style={{flex:1,minWidth:0,display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical',overflow:'hidden',fontSize:'var(--t-xs)',lineHeight:'1.8',wordBreak:'break-word',whiteSpace:'normal'}}>{stripHtml(f.title||f.question||'')}</a>
                        </div>
                      ))
                    : <NoContent icon="📭" msgKey="fatwas" />
                }
              </div>
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}